/* eslint-disable react/button-has-type */
import classNames from 'classnames';
import { useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import TrackingContext from '../../lib/TrackingContext';

import Button from './Button';

import styles from '../../../styles/buttons/buy-tickets.module.css';

type PricingType = 'paid' | 'free' | 'registration' | false;
type StatusType = 'scheduled' | 'cancelled' | 'postponed' | null;

interface BuyTicketsButtonProps {
    status?: StatusType;
    label?: React.ReactNode;
    labelFree?: React.ReactNode;
    labelRegister?: React.ReactNode;
    pricing?: PricingType;
    href?: string | null;
    onClick?: ((e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void) | null;
    className?: string | null;
}

function BuyTicketsButton({
    status = null,
    label = (
        <FormattedMessage
            defaultMessage="Acheter des billets"
            description="Buy tickets button label"
        />
    ),
    labelFree = (
        <FormattedMessage
            defaultMessage="En savoir plus"
            description="Learn more button label for free events"
        />
    ),
    labelRegister = (
        <FormattedMessage
            defaultMessage="S'inscrire à l'événement"
            description="Register for event button label"
        />
    ),
    pricing = false,
    className = null,
    href = null,
    onClick = null,
    ...props
}: BuyTicketsButtonProps) {
    const { trackEvent } = useContext(TrackingContext);

    let finalLabel: React.ReactNode = labelFree;
    const disabled = status !== null && status !== 'scheduled';

    if (pricing === 'paid') {
        finalLabel = label;
    }
    if (pricing === 'registration') {
        finalLabel = labelRegister;
    }

    if (disabled) {
        if (status === 'cancelled') {
            finalLabel = (
                <FormattedMessage defaultMessage="Annulé" description="Event cancelled status" />
            );
        } else if (status === 'postponed') {
            finalLabel = (
                <FormattedMessage defaultMessage="Reporté" description="Event postponed status" />
            );
        }
    }

    const onClickLink = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            trackEvent('BuyTickets', { action: 'click', label: href });
            if (onClick !== null) {
                onClick(e);
            }
        },
        [onClick, trackEvent, href],
    );

    return finalLabel !== null ? (
        <Button
            className={classNames([
                styles.container,
                {
                    [styles.disabled]: disabled,
                    [className]: className !== null,
                },
            ])}
            disabled={disabled}
            href={href}
            onClick={onClickLink}
            label={finalLabel}
            {...props}
        />
    ) : null;
}

export default BuyTicketsButton;
